// EST-FEAT-04 — Early Quick Quote Generation, persistence side.
// Backend: api-service app/api/v1/endpoints/quick_quotes.py, app/domain/quick_quotes/models.py
//
// Distinct from `useGetPropertyRoofEstimateQuery` (workspaceApiExtensions), which is the
// STATELESS property-scoped calculation powering the satellite/trace surface — no record, no
// engagement. This slice is the engagement-scoped RECORD: EST-FR-04-005 requires the quote to be
// retained against the Engagement, and EST-AC-04-004 requires it to stay distinct from the formal
// estimate and proposal.
//
// Preview and save are two separate calls on purpose (EST-FR-04-003 — "regenerate ... before
// saving it"): preview is a mutation rather than a query because it takes a body and must never be
// cached or auto-refetched — each call is an explicit "recalculate with these inputs" the user
// asked for, and an abandoned exploration must leave nothing behind.
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/redux/common/baseQuery';

interface ApiResponse<T> {
  success: boolean;
  message_code: number;
  message: string;
  data: T;
}

interface ListResponse<T> extends ApiResponse<T[]> {
  total: number;
}

/** Where the roof area came from. Kept visible so an eyeballed figure never looks measured. */
export type QuickQuoteAreaSource = 'AI_SOLAR' | 'MANUAL';

/** Whether the complexity multiplier was the tenant's configured value or a per-job override. */
export type QuickQuoteComplexitySource = 'CONFIG' | 'AGENT_OVERRIDE';

export interface QuickQuoteInputs {
  area_sqft: number;
  area_source: QuickQuoteAreaSource;
  /** Set only for an AI_SOLAR area — when the imagery behind the measurement was captured. */
  imagery_date?: string | null;
}

export interface QuickQuotePricingBasis {
  unit: string;
  price_per_unit: number;
  complexity_factor: number;
  complexity_source: QuickQuoteComplexitySource;
}

export interface QuickQuoteResult {
  billable_quantity: number;
  estimated_cost_low: number;
  estimated_cost_high: number;
}

/** An unsaved calculation — no identity fields, so the same component renders it and a record. */
export interface QuickQuotePreview {
  engagement_id: string;
  property_id: string;
  inputs: QuickQuoteInputs;
  pricing_basis: QuickQuotePricingBasis;
  result: QuickQuoteResult;
  is_indicative: true;
  record_type: 'QUICK_QUOTE';
}

export interface QuickQuote extends QuickQuotePreview {
  quick_quote_code: string;
  version_no: number;
  is_current: boolean;
  active: boolean;
  created_by?: string | null;
  created_at?: string | null; // ISO datetime
}

/**
 * Only the two adjustable inputs. The pricing VALUES (price_per_unit/unit) are tenant-configured
 * per EST-CFG-TNT-04-001 and are deliberately not part of this request — they're edited through
 * the config catalogue (QuickQuotePricingForm), and the backend refuses to take them from a quote
 * request so a client can't quietly discount a job.
 */
export interface QuickQuoteAdjustments {
  /** Overrides the property's measured area; omit to use the AI_SOLAR measurement. */
  area_sqft?: number;
  /** Per-job complexity judgement; omit to use the tenant's configured factor. */
  complexity_factor?: number;
  /** Per-quote rate override; omit to use the tenant's configured price_per_unit. */
  price_per_unit?: number;
}

export const quickQuotesApi = createApi({
  reducerPath: 'quickQuotesApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['QuickQuote'],
  endpoints: (builder) => ({
    /** EST-FR-04-003 — calculate without persisting. Invalidates nothing: there is no record. */
    previewQuickQuote: builder.mutation<QuickQuotePreview, { engagementId: string; adjustments?: QuickQuoteAdjustments }>({
      query: ({ engagementId, adjustments }) => ({
        url: `/api/v1/engagements/${engagementId}/quick-quotes/preview`,
        method: 'POST',
        body: adjustments ?? {},
      }),
      transformResponse: (res: ApiResponse<QuickQuotePreview>) => res.data,
    }),

    /** EST-FR-04-005 / EST-AC-04-004 — retain against the engagement as a new version. */
    saveQuickQuote: builder.mutation<QuickQuote, { engagementId: string; adjustments?: QuickQuoteAdjustments }>({
      query: ({ engagementId, adjustments }) => ({
        url: `/api/v1/engagements/${engagementId}/quick-quotes`,
        method: 'POST',
        body: adjustments ?? {},
      }),
      transformResponse: (res: ApiResponse<QuickQuote>) => res.data,
      // A save supersedes the previous version, so both the current quote and the version history
      // are stale — invalidating the engagement's tag covers both.
      invalidatesTags: (_r, _e, { engagementId }) => [{ type: 'QuickQuote' as const, id: engagementId }],
    }),

    /** Null when this engagement has no saved quote — never an error (EST-AC-04-005). */
    getCurrentQuickQuote: builder.query<QuickQuote | null, string>({
      query: (engagementId) => `/api/v1/engagements/${engagementId}/quick-quotes/current`,
      transformResponse: (res: ApiResponse<QuickQuote | null>) => res.data,
      providesTags: (_r, _e, engagementId) => [{ type: 'QuickQuote' as const, id: engagementId }],
    }),

    /** Newest first, superseded versions included — what was quoted earlier stays visible. */
    listQuickQuotes: builder.query<QuickQuote[], string>({
      query: (engagementId) => `/api/v1/engagements/${engagementId}/quick-quotes`,
      transformResponse: (res: ListResponse<QuickQuote>) => res.data ?? [],
      providesTags: (_r, _e, engagementId) => [{ type: 'QuickQuote' as const, id: engagementId }],
    }),
  }),
});

export const {
  usePreviewQuickQuoteMutation,
  useSaveQuickQuoteMutation,
  useGetCurrentQuickQuoteQuery,
  useListQuickQuotesQuery,
} = quickQuotesApi;
